import localesConfig from '../../config/locales';

export const DEFAULT_LOCALE: string = localesConfig.defaultLocale;
export const SUPPORTED_LOCALES: string[] = localesConfig.locales;

const BACKEND_CODES: Record<string, string> = localesConfig.backendCodes || {};

// Frontend locale -> the code the backend expects in its `locale` param ('cs' -> 'ch'). Other values,
// including codes that already are backend codes, are returned unchanged.
export function toBackendLocale ( locale: any ): any {
  const value = typeof locale === 'string' ? locale.toLowerCase() : locale;
  return BACKEND_CODES[value] ?? locale;
}

// Backend language code (e.g. the language list's 'CH') -> frontend locale ('cs').
export function fromBackendLocale ( code: any ): string {
  const value = String( code ).toLowerCase();
  const found = Object.keys( BACKEND_CODES ).find( ( locale ) => BACKEND_CODES[locale] === value );
  return found ?? value;
}

// Per-request locale for server side data fetching. getServerSideProps and the /apiloggia proxy run
// their callbacks inside runWithLocale(), so DataFetchHelper.fetchData can read the locale without
// every call site having to pass it down. AsyncLocalStorage only exists on the server; the
// require is hidden from webpack so the client bundle (which imports DataFetchHelper) still builds.
let storage: any = null;
if ( typeof window === 'undefined' ) {
  // eslint-disable-next-line no-eval, @typescript-eslint/no-var-requires
  const { AsyncLocalStorage } = eval( 'require' )( 'async_hooks' );
  storage = new AsyncLocalStorage();
}

export function normalizeLocale ( locale: any ): string {
  const value = typeof locale === 'string' ? locale.trim().toLowerCase() : '';
  return SUPPORTED_LOCALES.includes( value ) ? value : DEFAULT_LOCALE;
}

export function runWithLocale<T> ( locale: any, callback: () => T ): T {
  if ( storage == null ) {
    return callback();
  }
  return storage.run( { locale: normalizeLocale( locale ) }, callback );
}

// Locale of the current server request, or null when it is the default locale.
// The default locale sends no `locale` param, which keeps today's backend responses and Redis keys
// untouched for sites that never use a second language.
export function getRequestLocale (): string | null {
  const locale = storage != null ? storage.getStore()?.locale : null;
  // the value goes to the backend (locale param, Redis keys), so it is the backend's code for the language
  return locale && locale !== DEFAULT_LOCALE ? toBackendLocale( locale ) : null;
}

// A language the site has not published (Lodge > languages) is not served: getServerSideProps answers with a
// redirect to the same page in the default language. The site's languages are the navigation's `lang` entries,
// which the backend lists only when the site has more than one published language.
export function unpublishedLocaleRedirect ( context: any, result: any ): any {
  const locale = normalizeLocale( context.locale );
  const navigation = result?.props?.navigation;
  if ( locale === DEFAULT_LOCALE || navigation == null || typeof navigation !== 'object' ) {
    return null;
  }

  const entries = Array.isArray( navigation.partials?.lang?.entries ) ? navigation.partials.lang.entries : [];
  if ( entries.some( ( entry ) => fromBackendLocale( entry?.code ) === locale ) ) {
    return null;
  }

  // the address the visitor asked for (a friendly url the middleware rewrote stays friendly), without the
  // locale prefix; client side navigations come as /_next/data/... and use the page's resolved url instead
  const requestUrl = typeof context.req?.url === 'string' && !context.req.url.startsWith( '/_next/' ) ? context.req.url : context.resolvedUrl;
  const destination = requestUrl.replace( new RegExp( '^/' + locale + '(?=/|\\?|$)' ), '' ) || '/';

  return { redirect: { destination: destination.startsWith( '/' ) ? destination : '/' + destination, permanent: false } };
}
